import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AuthSkyline } from '../components/common/AuthSkyline';
import {
  PageShell,
  ProfilePageWrapper,
  ContentArea,
  WarningWrapper,
  LeftColumnContainer,
  RightColumnScrollArea,
  MobileNavRow,
  MobileNavButton,
  DesktopLayout,
  MobileLayout,
  ProfilePhotoCard,
  CardHeader,
  CardContent,
} from '../styles/profile/layout.style';

// --- Components ---
import { ProfileSidebar, TabConfig } from '../components/profile/ProfileSidebar';
import { ProfilePhoto } from '../components/profile/ProfilePhoto';
import { ProfileDetails } from '../components/profile/ProfileDetails';
import { ProfileDescription } from '../components/profile/ProfileDescription';
import { PayeeDetailsSection } from '../components/profile/PayeeDetailsSection';
import { SettingsSection } from '../components/profile/SettingsSection';
import { ProfileIncompleteWarning } from '../components/register/InfoBlock';

// --- Context & Utils ---
import { useUser } from '../contextManager/user.context';
import { useScale } from './HeightScaler';
import { getTutorProfileStatus } from '../utils';

// --- Icons ---
import {
  PersonFill,
  CardText,
  FilePerson,
  PersonVcardFill,
  CreditCard2FrontFill,
  FileEarmarkTextFill,
  GearFill
} from 'react-bootstrap-icons';
import { VerificationBanner } from '../components/verification/VerificationBanner';
import { AccountPendingBanner } from '../components/verification/AccountPendingBanner';
import { VerificationRevokeWarning } from '../components/verification/VerificationRevokeWarning';

// --- CONFIGURATION ---
const PROFILE_TABS: readonly TabConfig[] = [
    {label: 'Personal Details', Icon: PersonVcardFill }, 
    {label: 'Payee Details', Icon: CreditCard2FrontFill}, 
    {label: 'Platform Rules', Icon: FileEarmarkTextFill },
    {label: 'Settings', Icon: GearFill },
];

const MOBILE_TABS = [
  { id: 'photo', label: 'Photo', Icon: PersonFill },
  { id: 'details', label: 'Details', Icon: CardText },
  { id: 'bio', label: 'Bio', Icon: FilePerson },
] as const;

export default function ProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Drive the active tab off the URL so links like /profile?tab=Settings work
  // even when you're already on the profile page (avatar menu, nav, etc.).
  const activeTab = searchParams.get('tab') || 'Personal Details';
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams(tab === 'Personal Details' ? {} : { tab }, { replace: true });
  }, [setSearchParams]);
  const {userDetails} = useUser();
  const {scale} = useScale();
  
  // Desktop Accordion State
  const [openSections, setOpenSections] = useState({
    photo: true,
    details: false,
    description: true,
  });

  // Mobile Focus Mode State
  const [mobileTab, setMobileTab] = useState<'photo' | 'details' | 'bio'>('photo');

  // ── Verification threat tracking ──────────────────────────────
  const [verificationThreats, setVerificationThreats] = useState<Set<string>>(new Set());

  const handleVerificationThreat = useCallback((field: string, isThreatened: boolean) => {
    setVerificationThreats(prev => {
      const next = new Set(prev);
      if (isThreatened) {
        next.add(field);
      } else {
        next.delete(field);
      }
      // Only update if actually changed
      if (next.size === prev.size && Array.from(next).every(f => prev.has(f))) return prev;
      return next;
    });
  }, []);

  const showRevokeWarning = !!userDetails?.account_verified && verificationThreats.size > 0;

  // Logic to auto-open sections on large screens
  useEffect(() => {
    const height = window.innerHeight;
    if (height > 900) {
      setOpenSections({ photo: true, details: true, description: true });
    } else {
      setOpenSections({ photo: true, details: false, description: true });
    }
  }, []);

  const toggleSection = (key: keyof typeof openSections) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const { isComplete, missingFields } = useMemo(
    () => getTutorProfileStatus(userDetails),
    [userDetails]
  );
  const shouldShowWarning = !isComplete ||
    (userDetails?.role === 'tutor' && userDetails?.isProfileComplete && !userDetails?.account_verified)
    || showRevokeWarning;

  const showWarningArea = !isComplete || !userDetails?.email_verified || 
    (userDetails?.role === 'tutor' && userDetails?.isProfileComplete && !userDetails?.account_verified)
    || showRevokeWarning;

  return (
    <>
    <Helmet><title>Profile - tute</title></Helmet>
    <PageShell css={{ height: `calc(100dvh / ${scale})`, position: 'relative' }}>
      <AuthSkyline light />
      <ProfilePageWrapper>
        <ProfileSidebar 
            activeTab={activeTab} 
            onTabChange={setActiveTab} 
            tabs={PROFILE_TABS}
        />
        <ContentArea hasWarning={shouldShowWarning}>
        {showWarningArea && (
          <WarningWrapper>
            <AccountPendingBanner />
            <VerificationBanner />
            {!isComplete && <ProfileIncompleteWarning missingFields={missingFields}/>}
            {showRevokeWarning && (
              <VerificationRevokeWarning threats={Array.from(verificationThreats)} />
            )}
          </WarningWrapper>
        )}
        {activeTab === 'Personal Details' && (
          <>
            {/* --- MOBILE VIEW (Focus Mode) --- */}
            <MobileLayout>
                <MobileNavRow>
                  {MOBILE_TABS.map((tab) => {
                    const isActive = mobileTab === tab.id;
                    return (
                      <MobileNavButton
                        active={isActive}
                        key={tab.id}
                        onClick={() => setMobileTab(tab.id as any)}
                      >
                        <tab.Icon />
                        <span>{tab.label}</span>
                      </MobileNavButton>
                    )
                  })}
                </MobileNavRow>

                    {mobileTab === 'photo' && (
                        <ProfilePhoto 
                            isOpen={true} 
                            onToggle={() => {}} 
                            isMobile={true}
                        />
                    )}
                    
                    {mobileTab === 'details' && (
                        <ProfileDetails 
                          isOpen={true} 
                          isMobile={true} 
                          onToggle={() => {}} 
                          onVerificationThreat={handleVerificationThreat}
                        />
                    )}

                    {mobileTab === 'bio' && (
                        <ProfileDescription 
                          isMobile={true} 
                          isOpen={true} 
                          onToggle={() => {}}
                          onVerificationThreat={handleVerificationThreat}
                        />
                    )}
            </MobileLayout>

            {/* --- DESKTOP VIEW (Accordion Stack) --- */}
            <DesktopLayout>
                <LeftColumnContainer>
                    <ProfilePhoto 
                        isOpen={openSections.photo} 
                        onToggle={() => toggleSection('photo')}
                    />
                    <ProfileDetails
                        isOpen={openSections.details}
                        onToggle={() => toggleSection('details')}
                        onVerificationThreat={handleVerificationThreat}
                    />
                </LeftColumnContainer>
                
                <RightColumnScrollArea>
                    <ProfileDescription
                      isMobile={false}
                      isOpen={openSections.description}
                      onToggle={() => toggleSection('description')}
                      onVerificationThreat={handleVerificationThreat}
                    />
                </RightColumnScrollArea>
            </DesktopLayout>
          </>
        )}

        {/* --- OTHER TABS --- */}
        {activeTab === 'Payee Details' && (
          <div style={{ gridColumn: '1', alignSelf: 'start' }}>
            <PayeeDetailsSection />
          </div>
        )}

        {activeTab === 'Platform Rules' && (
          <div style={{ gridColumn: '1', alignSelf: 'start' }}>
            <ProfilePhotoCard>
              <CardHeader>Platform Rules</CardHeader>
              <CardContent>
                <div style={{ display: 'grid', gap: '1rem', fontSize: '0.85rem', color: 'var(--colors-sand12)' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.35rem', fontWeight: 600 }}>Cancellation Policy</h4>
                    <p style={{ margin: 0, color: 'var(--colors-sand11)', lineHeight: 1.5 }}>
                      Any upcoming lesson can be cancelled for free before it starts — you're only ever charged once a lesson is completed. Lessons that have already taken place can't be cancelled.
                    </p>
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 0.35rem', fontWeight: 600 }}>Payment</h4>
                    <p style={{ margin: 0, color: 'var(--colors-sand11)', lineHeight: 1.5 }}>
                      The parent's card is securely saved at booking but not charged. Payment for each lesson is taken only once it is completed — when the tutor provides feedback.
                    </p>
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 0.35rem', fontWeight: 600 }}>Conduct</h4>
                    <p style={{ margin: 0, color: 'var(--colors-sand11)', lineHeight: 1.5 }}>
                      All tutors are expected to provide professional, respectful, and high-quality teaching. Tute reserves the right to remove any tutor who does not meet these standards.
                    </p>
                  </div>
                </div>
              </CardContent>
            </ProfilePhotoCard>
          </div>
        )}

        {activeTab === 'Settings' && <SettingsSection />}

        </ContentArea>
      </ProfilePageWrapper>
    </PageShell>
    </>
  );
}